import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  Blocks,
  X,
  ChevronUp,
  ChevronDown,
  Check,
} from 'lucide-react';
import { fieldTemplates, ApiError, type FieldTemplateRow } from '@/api/client';
import { PageHeader } from '@/components/layout/PageHeader';
import { errorText } from '@/lib/permissionError';

/**
 * Field-Template Library — admin CRUD for the palette catalog that
 * feeds the Form Designer's drag-drop sidebar. Templates are tenant-
 * scoped and each carries a JSON blob of field definitions.
 */

const FIELD_TYPES = [
  'text',
  'number',
  'date',
  'datetime',
  'select',
  'radio',
  'checkbox',
  'textarea',
  'calculated',
] as const;
type FieldType = (typeof FIELD_TYPES)[number];

interface EditorField {
  field_key: string;
  field_label: string;
  field_type: FieldType;
  required: boolean;
  help_text: string;
  /** Newline-separated. Simple form: `value=label`. Extended form for
   *  CDISC exports: `value=label|code=X|system=Y`. Both forms mix
   *  freely; the parser trims and ignores empty pieces. */
  options: string;
  /** CDISC / SDTM item OID — the machine-readable identifier used by
   *  SDTM/ADaM exports and Define-XML. Optional; only matters when the
   *  study is submitted to a regulator or transferred to a sponsor's
   *  data warehouse. Stored inside `config.field_oid` on the field
   *  definition. */
  field_oid: string;
}

export interface EditorState {
  field_template_id: string | null;
  template_key: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  is_active: boolean;
  fields: EditorField[];
}

export const EMPTY_EDITOR: EditorState = {
  field_template_id: null,
  template_key: '',
  name: '',
  description: '',
  category: '',
  icon: '',
  is_active: true,
  fields: [],
};

const TEMPLATE_KEY_RE = /^[a-z][a-z0-9-]{1,63}$/;
const FIELD_KEY_RE = /^[a-z][a-z0-9_]{0,59}$/;

/**
 * CDISC-related inputs (Field OID per-field, CDISC-code syntax in
 * options) are hidden from the editor UI for now. The state field
 * (`EditorField.field_oid`) and the pipe-syntax parsers remain — this
 * keeps data round-tripping intact for any templates that already
 * carry OIDs from a future admin flow or a seeded catalog. Flip this
 * to `true` when SDTM/ADaM export becomes a real requirement.
 */
const SHOW_CDISC_FIELDS = false;

// ---------------------------------------------------------------------------

export function FieldTemplatesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState<EditorState | null>(null);

  const listQ = useQuery({
    queryKey: ['field-templates'],
    queryFn: () => fieldTemplates.list(),
  });

  const remove = useMutation({
    mutationFn: (id: string) => fieldTemplates.remove(id),
    onSuccess: () => {
      toast.success('Template deleted');
      qc.invalidateQueries({ queryKey: ['field-templates'] });
    },
    onError: (e) => toast.error(errorText(e, 'Delete failed')),
  });

  const templates = listQ.data?.templates ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.template_key.toLowerCase().includes(q) ||
        (t.category ?? '').toLowerCase().includes(q),
    );
  }, [search, templates]);

  const grouped = useMemo(() => {
    const buckets = new Map<string, FieldTemplateRow[]>();
    for (const t of filtered) {
      const key = t.category?.trim() || 'Uncategorised';
      const arr = buckets.get(key) ?? [];
      arr.push(t);
      buckets.set(key, arr);
    }
    return Array.from(buckets.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <div className="space-y-4">
      <PageHeader
        backTo="/admin"
        title="Field templates"
        subtitle="Reusable field groups for the Form Designer palette. Drop them onto any form to insert their fields in one step."
        actions={
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => setEditor({ ...EMPTY_EDITOR })}
          >
            <Plus className="h-3.5 w-3.5" /> New template
          </button>
        }
      />

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-muted" />
          <input
            type="search"
            placeholder="Search by name, key, or category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-7 text-xs"
          />
        </div>
        <span className="text-xs text-ink-muted">
          {filtered.length} of {templates.length}
        </span>
      </div>

      {listQ.isLoading && (
        <div className="card flex items-center justify-center gap-2 p-10 text-xs text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
        </div>
      )}

      {!listQ.isLoading && templates.length === 0 && (
        <div className="card p-8 text-center text-sm text-ink-muted">
          <Blocks className="mx-auto mb-2 h-8 w-8 text-ink-muted/60" />
          <p className="mb-1 font-medium text-ink-primary">No templates yet</p>
          <p className="text-xs">
            Create your first reusable field-group. It'll appear in the Form Designer sidebar
            whenever a designer edits a form.
          </p>
        </div>
      )}

      {grouped.map(([category, rows]) => (
        <section key={category} className="card overflow-hidden">
          <div className="border-b border-surface-border bg-surface-muted/40 px-3 py-1.5 text-xs uppercase tracking-wider text-ink-muted">
            {category}
            <span className="ml-1 text-[10px]">({rows.length})</span>
          </div>
          <ul className="divide-y divide-surface-border">
            {rows.map((t) => (
              <li key={t.field_template_id} className="flex items-center gap-3 px-3 py-2 text-xs">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="font-medium text-ink-primary">{t.name}</span>
                    <span className="font-mono text-[10px] text-ink-muted">{t.template_key}</span>
                    <span className="text-[10px] text-ink-muted">·</span>
                    <span className="text-[10px] text-ink-muted">
                      {templateFieldCount(t.template_fields_json)} fields
                    </span>
                    {!t.is_active && (
                      <span className="rounded bg-ink-muted/20 px-1.5 py-0.5 text-[10px] font-medium text-ink-muted">
                        retired
                      </span>
                    )}
                  </div>
                  {t.description && (
                    <div className="mt-0.5 truncate text-[11px] text-ink-muted">
                      {t.description}
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    className="rounded p-1 text-ink-muted hover:bg-surface-muted hover:text-ink-primary"
                    onClick={() => openEdit(t, setEditor)}
                    title="Edit template"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    className="rounded p-1 text-ink-muted hover:bg-danger-50 hover:text-danger-700 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Delete template "${t.name}"? Forms that already use it are unaffected — they carry their own copies of the fields.`,
                        )
                      ) {
                        remove.mutate(t.field_template_id);
                      }
                    }}
                    title="Delete template"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {editor && (
        <TemplateEditorModal
          state={editor}
          setState={setEditor}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            qc.invalidateQueries({ queryKey: ['field-templates'] });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function openEdit(t: FieldTemplateRow, setEditor: (s: EditorState) => void) {
  setEditor({
    field_template_id: t.field_template_id,
    template_key: t.template_key,
    name: t.name,
    description: t.description ?? '',
    category: t.category ?? '',
    icon: t.icon ?? '',
    is_active: t.is_active,
    fields: parseTemplateJson(t.template_fields_json),
  });
}

function templateFieldCount(json: string): number {
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
}

function parseTemplateJson(json: string): EditorField[] {
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((raw: Record<string, unknown>): EditorField => {
      const config = (raw.config as Record<string, unknown>) ?? {};
      return {
        field_key: String(raw.field_key ?? ''),
        field_label: String(raw.field_label ?? ''),
        field_type: (FIELD_TYPES.includes(raw.field_type as FieldType)
          ? (raw.field_type as FieldType)
          : 'text') as FieldType,
        required: Boolean(config.required),
        help_text: String(config.help_text ?? ''),
        options: optionsToString(config.options),
        field_oid: String(config.field_oid ?? ''),
      };
    });
  } catch {
    return [];
  }
}

function optionsToString(raw: unknown): string {
  if (!Array.isArray(raw)) return '';
  return raw
    .map((o: unknown) => {
      const obj = (o as Record<string, unknown>) ?? {};
      const value = String(obj.value ?? '');
      const label = String(obj.label ?? '');
      const code = String(obj.code ?? '');
      const codeSystem = String(obj.code_system ?? '');
      const head = label && label !== value ? `${value}=${label}` : value;
      const tail: string[] = [];
      if (code) tail.push(`code=${code}`);
      if (codeSystem) tail.push(`system=${codeSystem}`);
      return tail.length ? `${head}|${tail.join('|')}` : head;
    })
    .filter(Boolean)
    .join('\n');
}

function optionsToArray(
  raw: string,
): Array<{ value: string; label: string; code?: string; code_system?: string }> {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [main = '', ...rest] = line.split('|').map((p) => p.trim());
      const eq = main.indexOf('=');
      const value = eq >= 0 ? main.slice(0, eq).trim() : main;
      const label = eq >= 0 ? main.slice(eq + 1).trim() : value;
      let code: string | undefined;
      let codeSystem: string | undefined;
      for (const p of rest) {
        if (p.startsWith('code=')) code = p.slice(5).trim();
        else if (p.startsWith('system=')) codeSystem = p.slice(7).trim();
      }
      return {
        value,
        label,
        ...(code ? { code } : {}),
        ...(codeSystem ? { code_system: codeSystem } : {}),
      };
    });
}

// ---------------------------------------------------------------------------
// Modal + editor — exported so the Form Designer palette can host its own
// "+ New template" flow without navigating to the admin page.
// ---------------------------------------------------------------------------

export function TemplateEditorModal({
  state,
  setState,
  onClose,
  onSaved,
}: {
  state: EditorState;
  setState: (s: EditorState) => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = state.field_template_id !== null;

  const save = useMutation({
    mutationFn: async () => {
      if (!TEMPLATE_KEY_RE.test(state.template_key)) {
        throw new Error('template_key must be kebab-case, 2-64 chars (e.g. race-multi-select)');
      }
      if (!state.name.trim()) throw new Error('Name is required');
      if (state.fields.length === 0) throw new Error('Add at least one field');
      const seenKeys = new Set<string>();
      for (const f of state.fields) {
        if (!FIELD_KEY_RE.test(f.field_key)) {
          throw new Error(
            `Field key "${f.field_key}" is invalid (a-z, 0-9, _; must start with a letter)`,
          );
        }
        if (seenKeys.has(f.field_key)) {
          throw new Error(`Duplicate field key: ${f.field_key}`);
        }
        seenKeys.add(f.field_key);
        if (!f.field_label.trim()) throw new Error('Every field needs a label');
      }
      const jsonPayload = JSON.stringify(
        state.fields.map((f, i) => ({
          field_key: f.field_key,
          field_label: f.field_label,
          field_type: f.field_type,
          field_order: i,
          config: {
            required: f.required || undefined,
            help_text: f.help_text.trim() || undefined,
            // CDISC / SDTM identifiers. Stored inside config so the
            // form-level publish schema (which validates the top-level
            // field-def shape only) passes them through unchanged.
            field_oid: f.field_oid.trim() || undefined,
            options: needsOptions(f.field_type) ? optionsToArray(f.options) : undefined,
          },
        })),
      );
      if (isEdit) {
        return fieldTemplates.update(state.field_template_id!, {
          template_key: state.template_key,
          name: state.name.trim(),
          description: state.description.trim(),
          category: state.category.trim(),
          icon: state.icon.trim(),
          template_fields_json: jsonPayload,
          is_active: state.is_active,
        });
      }
      return fieldTemplates.create({
        template_key: state.template_key,
        name: state.name.trim(),
        description: state.description.trim() || undefined,
        category: state.category.trim() || undefined,
        icon: state.icon.trim() || undefined,
        template_fields_json: jsonPayload,
        is_active: state.is_active,
      });
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Template updated' : 'Template created');
      onSaved();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : (e as Error).message),
  });

  function patch<K extends keyof EditorState>(key: K, value: EditorState[K]) {
    setState({ ...state, [key]: value });
  }

  function addField() {
    const next: EditorField = {
      field_key: `field_${state.fields.length + 1}`,
      field_label: '',
      field_type: 'text',
      required: false,
      help_text: '',
      options: '',
      field_oid: '',
    };
    setState({ ...state, fields: [...state.fields, next] });
  }

  function updateField(idx: number, patchField: Partial<EditorField>) {
    setState({
      ...state,
      fields: state.fields.map((f, i) => (i === idx ? { ...f, ...patchField } : f)),
    });
  }

  function removeField(idx: number) {
    setState({ ...state, fields: state.fields.filter((_, i) => i !== idx) });
  }

  function moveField(idx: number, dir: -1 | 1) {
    const next = [...state.fields];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    setState({ ...state, fields: next });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center !m-0 p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-center justify-between border-b border-surface-border px-4 py-2.5">
          <h2 className="text-sm font-semibold text-ink-primary">
            {isEdit ? `Edit template — ${state.name || state.template_key}` : 'New field template'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {/* Metadata */}
          <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">
                Name <span className="text-danger-500">*</span>
              </span>
              <input
                type="text"
                className="input mt-1 text-sm"
                value={state.name}
                onChange={(e) => patch('name', e.target.value)}
                placeholder="e.g. Race (multi-select)"
              />
            </label>
            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">
                Template key <span className="text-danger-500">*</span>
              </span>
              <input
                type="text"
                className="input mt-1 font-mono text-sm"
                value={state.template_key}
                onChange={(e) => patch('template_key', e.target.value.toLowerCase())}
                placeholder="race-multi-select"
              />
              <span className="mt-0.5 block text-[10px] text-ink-muted">
                kebab-case, unique per tenant
              </span>
            </label>
            <label className="block text-xs md:col-span-2">
              <span className="font-medium text-ink-secondary">Description</span>
              <textarea
                rows={2}
                className="input mt-1 text-sm"
                value={state.description}
                onChange={(e) => patch('description', e.target.value)}
                placeholder="Shown as a tooltip in the palette. Explain when to use this template."
              />
            </label>
            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">Category</span>
              <input
                type="text"
                className="input mt-1 text-sm"
                value={state.category}
                onChange={(e) => patch('category', e.target.value)}
                placeholder="Demographics, Vitals, Labs…"
              />
              <span className="mt-0.5 block text-[10px] text-ink-muted">
                Free text — groups templates in the palette
              </span>
            </label>
            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">Icon slug (optional)</span>
              <input
                type="text"
                className="input mt-1 font-mono text-sm"
                value={state.icon}
                onChange={(e) => patch('icon', e.target.value)}
                placeholder="user, thermometer, syringe…"
              />
              <span className="mt-0.5 block text-[10px] text-ink-muted">
                lucide-react icon name
              </span>
            </label>
            <label className="flex items-center gap-2 pt-4 text-xs md:col-span-2">
              <input
                type="checkbox"
                checked={state.is_active}
                onChange={(e) => patch('is_active', e.target.checked)}
              />
              <span className="font-medium text-ink-secondary">
                Active{' '}
                <span className="text-[10px] font-normal text-ink-muted">
                  — retired templates are hidden from the palette
                </span>
              </span>
            </label>
          </section>

          {/* Fields editor */}
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-semibold text-ink-primary">
                Fields{' '}
                <span className="text-[10px] font-normal text-ink-muted">
                  ({state.fields.length})
                </span>
              </h3>
              <button type="button" onClick={addField} className="btn-secondary text-xs">
                <Plus className="h-3 w-3" /> Add field
              </button>
            </div>

            {state.fields.length === 0 ? (
              <div className="rounded border border-dashed border-surface-border bg-surface-muted/20 p-6 text-center text-[11px] text-ink-muted">
                No fields yet. Click "Add field" to build the template body.
              </div>
            ) : (
              <ul className="space-y-2">
                {state.fields.map((f, idx) => (
                  <li
                    key={idx}
                    className="rounded border border-surface-border bg-surface-muted/20 p-3"
                  >
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      <label className="block text-xs">
                        <span className="font-medium text-ink-secondary">Label</span>
                        <input
                          type="text"
                          className="input mt-0.5 text-xs"
                          value={f.field_label}
                          onChange={(e) => updateField(idx, { field_label: e.target.value })}
                        />
                      </label>
                      <label className="block text-xs">
                        <span className="font-medium text-ink-secondary">Variable name (key)</span>
                        <input
                          type="text"
                          className="input mt-0.5 font-mono text-xs"
                          value={f.field_key}
                          onChange={(e) =>
                            updateField(idx, { field_key: e.target.value.toLowerCase() })
                          }
                        />
                      </label>
                      <label className="block text-xs">
                        <span className="font-medium text-ink-secondary">Type</span>
                        <select
                          className="input mt-0.5 text-xs"
                          value={f.field_type}
                          onChange={(e) =>
                            updateField(idx, { field_type: e.target.value as FieldType })
                          }
                        >
                          {FIELD_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="flex items-center gap-1.5 pt-4 text-xs">
                        <input
                          type="checkbox"
                          checked={f.required}
                          onChange={(e) => updateField(idx, { required: e.target.checked })}
                        />
                        <span>Required</span>
                      </label>
                      <label className="block text-xs md:col-span-2">
                        <span className="font-medium text-ink-secondary">Help text</span>
                        <input
                          type="text"
                          className="input mt-0.5 text-xs"
                          value={f.help_text}
                          onChange={(e) => updateField(idx, { help_text: e.target.value })}
                          placeholder="Optional tooltip shown next to the field"
                        />
                      </label>
                      {SHOW_CDISC_FIELDS && (
                        <label className="block text-xs md:col-span-2">
                          <span className="font-medium text-ink-secondary">
                            Field OID (CDISC){' '}
                            <span className="text-[10px] font-normal text-ink-muted">
                              — optional; used by SDTM/ADaM exports and Define-XML
                            </span>
                          </span>
                          <input
                            type="text"
                            maxLength={40}
                            className="input mt-0.5 font-mono text-xs"
                            value={f.field_oid}
                            onChange={(e) =>
                              updateField(idx, { field_oid: e.target.value.toUpperCase() })
                            }
                            placeholder="SUBJID, AETERM, RACE1, HEIGHT_VSORRES…"
                          />
                          <span className="mt-0.5 block text-[10px] text-ink-muted">
                            Machine-readable identifier the export layer references. Leave blank if
                            you're not doing SDTM/CSR exports yet.
                          </span>
                        </label>
                      )}
                      {needsOptions(f.field_type) && (
                        <label className="block text-xs md:col-span-2">
                          <span className="font-medium text-ink-secondary">Options</span>
                          <textarea
                            rows={4}
                            className="input mt-0.5 font-mono text-xs"
                            value={f.options}
                            onChange={(e) => updateField(idx, { options: e.target.value })}
                            placeholder={
                              'One per line. Format:\nvalue=Label\n\nExamples:\nY=Yes\nN=No\nWHITE=White\nBLACK=Black or African American'
                            }
                          />
                          <span className="mt-0.5 block text-[10px] text-ink-muted">
                            {`Format: \`value=Label\` per line. Just \`value\` reuses value as label.`}
                          </span>
                        </label>
                      )}
                    </div>
                    <div className="mt-2 flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => moveField(idx, -1)}
                        disabled={idx === 0}
                        className="rounded p-1 text-ink-muted hover:bg-surface-muted hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
                        title="Move up"
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveField(idx, 1)}
                        disabled={idx === state.fields.length - 1}
                        className="rounded p-1 text-ink-muted hover:bg-surface-muted hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
                        title="Move down"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeField(idx)}
                        className="rounded p-1 text-ink-muted hover:bg-danger-50 hover:text-danger-700"
                        title="Remove field"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-surface-border px-4 py-2.5">
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost text-xs"
            disabled={save.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary text-xs"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            {isEdit ? 'Save changes' : 'Create template'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function needsOptions(t: FieldType): boolean {
  return t === 'select' || t === 'radio' || t === 'checkbox';
}
